import { forwardRef, memo, type ReactNode } from 'react';
import { useToggle } from 'react-use';

import { EyeClosedIcon, EyeIcon } from '@air/design-icons';
import { IconButton } from '@air/primitive-icon-button';
import { Input, type InputProps } from '@air/primitive-input';
import { cn } from '@air/utils-tailwind';

export type PasswordInputWithVisibilityToggleProps = Omit<InputProps, 'type'> & {
  hideLabel?: string;
  leadingIcon?: ReactNode;
  showLabel?: string;
  toggleClassName?: string;
  toggleTestId?: string;
  wrapperClassName?: string;
};

const wrapperSizeClass: Record<NonNullable<InputProps['size']>, string> = {
  'extra-large': 'h-12',
  large: 'h-10',
  medium: 'h-8',
  small: 'h-6',
};

export const PasswordInputWithVisibilityToggle = memo(
  forwardRef<HTMLInputElement, PasswordInputWithVisibilityToggleProps>(
    (
      {
        className,
        hasError,
        hideLabel = 'Hide password',
        id,
        leadingIcon,
        showLabel = 'Show password',
        size = 'medium',
        toggleClassName = 'absolute top-1/2 right-1.5 z-10 -translate-y-1/2',
        toggleTestId,
        wrapperClassName,
        ...inputProps
      },
      forwardedRef,
    ) => {
      const [isVisible, toggleVisibility] = useToggle(false);

      return (
        <div
          className={cn(
            'relative flex w-full items-center rounded-4 border ads:border-input border-gray-5 ads:bg-input bg-gray-1 ads:focus-within:border-primary focus-within:border-blue-9 ads:focus-within:ring-focus focus-within:ring-1 focus-within:ring-blue-9',
            wrapperSizeClass[size],
            hasError &&
              'ads:border-error border-red-9 ads:focus-within:border-error focus-within:border-red-9 ads:focus-within:ring-error focus-within:ring-red-9',
            inputProps.disabled && 'cursor-not-allowed ads:border-input border-gray-5 ads:bg-input bg-gray-1',
            wrapperClassName,
          )}
        >
          {leadingIcon}
          <Input
            {...inputProps}
            className={cn(
              // biome-ignore lint/plugin: !h-full beats the Input component's own height so the password field fills the wrapper
              '!h-full min-h-0 w-full border-0 bg-transparent pr-9 shadow-none ring-0 focus:border-0 focus:ring-0',
              className,
            )}
            hasError={false}
            id={id}
            ref={forwardedRef}
            size={size}
            type={isVisible ? 'text' : 'password'}
          />
          <IconButton
            adsIcon
            appearance="ghost"
            aria-controls={id}
            aria-pressed={isVisible}
            className={toggleClassName}
            color="gray"
            data-testid={toggleTestId}
            disabled={inputProps.disabled}
            icon={isVisible ? EyeIcon : EyeClosedIcon}
            label={isVisible ? hideLabel : showLabel}
            onClick={toggleVisibility}
            size="medium"
            type="button"
          />
        </div>
      );
    },
  ),
);

PasswordInputWithVisibilityToggle.displayName = 'PasswordInputWithVisibilityToggle';
